import type { Contract } from '@/types/ContractType';
import type { Product } from '@/types/ProductType';
import type { SellerContractRecord } from '@/types/SellerContractType';

function contractIdFromRecord(record: SellerContractRecord): string {
  const raw = record.contractId;
  if (typeof raw === 'string') return raw;
  return String(
    (raw as { _id?: string })?._id ?? record.contractDetails?._id ?? ''
  );
}

function propertyIdFromRecord(record: SellerContractRecord): string {
  const raw = record.propertyId;
  if (typeof raw === 'string') return raw;
  return String(
    (raw as { _id?: string })?._id ?? record.propertyDetails?._id ?? ''
  );
}

/** Normalize SellerContract snapshot into Product + Contract for PropertyView. */
export function normalizeSellerContractRow(record: SellerContractRecord): {
  product: Product;
  contract: Contract;
  contractId: string;
} {
  const contractId = contractIdFromRecord(record);
  const propertyId = propertyIdFromRecord(record);
  const details = record.propertyDetails ?? ({} as Product);
  const contract = (record.contractDetails ?? {}) as Contract;

  const product: Product = {
    ...details,
    _id: propertyId || details._id || contractId,
    price: details.price ?? contract.offerSpecifics?.offerPrice ?? 0,
    contractStarted: true,
    contract: contractId,
    createdAt:
      details.createdAt ?? record.createdAt ?? new Date().toISOString(),
    updatedAt:
      details.updatedAt ?? record.updatedAt ?? new Date().toISOString(),
    transactionMode: details.transactionMode ?? 'purchase',
    address: details.address ?? {
      street: contract.sellerPropertyInfo?.propertyAddress ?? '',
      city: '',
      state: '',
      zip: '',
      fullAddress: contract.sellerPropertyInfo?.propertyAddress,
    },
  };

  return { product, contract, contractId };
}

/** Keep highest-stage / most recent row per propertyId. */
export function dedupeSellerContractRows(
  rows: SellerContractRecord[],
  stageFor: (contract: Contract) => number
): SellerContractRecord[] {
  const byProperty = new Map<string, SellerContractRecord>();

  for (const row of rows) {
    const { product, contract } = normalizeSellerContractRow(row);
    const pid = product._id;
    const existing = byProperty.get(pid);
    if (!existing) {
      byProperty.set(pid, row);
      continue;
    }
    const ex = normalizeSellerContractRow(existing);
    const stageA = stageFor(contract);
    const stageB = stageFor(ex.contract);
    const tsA = new Date(row.updatedAt ?? row.createdAt ?? 0).getTime();
    const tsB = new Date(
      existing.updatedAt ?? existing.createdAt ?? 0
    ).getTime();
    if (stageA > stageB || (stageA === stageB && tsA > tsB)) {
      byProperty.set(pid, row);
    }
  }

  return Array.from(byProperty.values());
}
